import React, {createContext, useContext, useMemo, useRef, useState} from 'react';
import {Dialog, Portal} from '@chakra-ui/react';
import miscUtil from "@/utils/miscUtil";

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

const defaultProps = (close) => ({
    preventScroll: true,
    placement: 'center',
    onPointerDownOutside: () => close(),
    onEscapeKeyDown: () => close()
});

export const ModalProvider = ({children}) => {

    const [forceRerender, setForceRender] = useState(Date.now());
    const [_isOpen, setIsOpen] = useState(false);
    const modals = useRef([]);
    /** 'xs' | 'sm' | 'md' | 'lg' | 'xl' | 'cover' | 'full' */
    const [size, setSize] = useState('md');
    const [currentModalIndex, setCurrentModalIndex] = useState(-1);

    /** Close all non keep state active modal or close until the give modal key
     * it can also be a number of modal to close
     * */
    const close = (until = null) => {
        setCurrentModalIndex(prev => {
            const newIndex = getIndexAfterClose(prev, until);
            if (newIndex === -1) {
                modals.current = [];
                setIsOpen(() => false);
            } else {
                modals.current = [...modals.current.slice(0, newIndex + 1)];
                setSize(_ => modals.current[newIndex].size);
                setProps(_ => modals.current[newIndex].props);
            }
            return newIndex;
        });
    };
    const [props, setProps] = useState(defaultProps(close));

    const isOpen = (key = null) => {
        return _isOpen && (!key || modals.current[currentModalIndex].key === key);
    }

    /**
     * canLooseState means that we don't need to render it back when it has been closed
     */
    const open = (content, size = 'md', _props = {}, canLooseState = false, key = null) => {
        key = key ?? `${Date.now()}-${Math.round(Math.random() * 1E9)}`;
        const props = {...defaultProps(close), ..._props};
        setSize(size);
        setProps(() => ({...defaultProps(close), ..._props}));
        const length = modals.current.length;
        modals.current = [...modals.current, {content, size, canLooseState, key, props}]
        setCurrentModalIndex(_ => length);
        setIsOpen(() => true);
        return key;
    };

    const updateContent = (content) => {
        const newModals = [...modals.current];
        newModals[currentModalIndex] = {
            ...newModals[currentModalIndex],
            content,
        };

        modals.current = newModals;
        setForceRender(() => Date.now());
    };

    /**
     * Until can be a modal key or a number of modal (from current to first)
     * @param current
     * @param until
     * @returns {number}
     */
    const getIndexAfterClose = (current, until) => {
        const type = miscUtil.getValueType(until);
        for (let i = 1; (current - i) >= 0 && (current - i) < modals.current.length; i++) {
            if ((type === 'string' && modals.current[(current - i)].key === until) || (type === 'integer' && i === until) || !modals.current[(current - i)].canLooseState) {
                return current - i;
            }
        }
        return -1;
    }

    const closeAll = () => {
        modals.current = [];
        setSize(_ => 'md');
        setProps(_ => defaultProps(close));
        setCurrentModalIndex(_ => -1);
    }

    const currentModal = useMemo(() => {
        return modals[currentModalIndex] ?? null;
    }, [modals, currentModalIndex])

    return (
        <ModalContext.Provider value={{open, close, closeAll, isOpen, updateContent}}>
            {
                /**
                 * TrapFocus is necessary to avoid problem within safari
                 * cf: https://github.com/chakra-ui/ark/issues/1782#issuecomment-1840281884
                 */
            }
            <Dialog.Root lazyMount open={_isOpen} trapFocus={false} size={size} {...props}>
                <Portal>
                    <Dialog.Backdrop/>
                    <Dialog.Positioner pointerEvents="none">
                        <Dialog.Content
                            {...(currentModal?.size === 'full' ? {} : {maxW: '98vw'})}
                        >
                            <Dialog.Body overflow="hidden">
                                {modals.current.map((modal, index) => (
                                    <div
                                        id={`${modal.key}-${forceRerender}`}
                                        key={`modal-${modal.key}`}
                                        style={{
                                            display: index === currentModalIndex ? 'flex' : 'none',
                                            pointerEvents: index === currentModalIndex ? 'auto' : 'none',
                                            maxHeight: '100%',
                                            height: '100%'
                                        }}
                                    >
                                        {modal.content}
                                    </div>
                                ))}
                            </Dialog.Body>
                        </Dialog.Content>
                    </Dialog.Positioner>
                </Portal>
            </Dialog.Root>
            {children}
        </ModalContext.Provider>
    );
};

export default ModalProvider;
